import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogContent,
    DialogActions,
    Grid,
    Avatar,
    Rating,
    Chip,
    CircularProgress,
    Alert,
    TextField
} from '@mui/material';
import {
    Eye,
    User,
    X,
    Search,
    AlertTriangle,
    Star
} from 'lucide-react';
import {
    getAllReviews,
    getPlatformRatingSummary,
    updateReviewStatus,
    replyToReview
} from '../../Services/ReviewManagement/ReviewApis';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';

// ==========================================
// RATING SUMMARY & SITE ANALYTICS CONFIGURATION
// Colors retained exactly as designed:
// 5★ / 4★ & Scores >= 4.2: Sage Green (#6E8964)
// 3★ & Scores 3.8 - 4.1: Amber / Warm Gold (#FFBD65 / #D97706)
// 2★ & Scores < 3.8: Terracotta (#803927)
// 1★: Neutral Slate (#CBD5E1)
// ==========================================


const STAR_COLORS = {
    5: '#6E8964', // Sage Green
    4: '#6E8964', // Sage Green
    3: '#FFBD65', // Amber / Warm Gold
    2: '#803927', // Terracotta
    1: '#CBD5E1'  // Neutral Slate
};

const getStarColor = (stars) => STAR_COLORS[stars] || '#CBD5E1';

const CATEGORY_COLORS = {
    'Location': '#6E8964',
    'Accuracy': '#6E8964',
    'Teachers': '#6E8964',
    'Food': '#6E8964',
    'Communication': '#D97706',
    'Value': '#D97706',
    'Accommodation': '#803927'
};

const getCategoryColor = (category, score) => {
    if (CATEGORY_COLORS[category]) return CATEGORY_COLORS[category];
    const num = parseFloat(score) || 0;
    if (num >= 4.2) return '#6E8964';
    if (num >= 3.8) return '#D97706';
    return '#803927';
};

const Reviews = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useGlobalSnackbar();

    // Data states
    const [reviews, setReviews] = useState([]);
    const [statusCounts, setStatusCounts] = useState({
        all: 0,
        pending: 0,
        publish: 0,
        reject: 0,
        isexternalLinkByHost: 0
    });
    const [loading, setLoading] = useState(true);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [error, setError] = useState(null);

    // Platform Rating Summary states
    const [ratingSummary, setRatingSummary] = useState();
    const [loadingSummary, setLoadingSummary] = useState(false);

    // Filter states
    // activeTab: 'pending' (status: pending) | 'publish' (status: publish) | 'external' (isexternalLinkByHost: true)
    const [activeTab, setActiveTab] = useState('pending');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Reject Confirmation Modal states
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [reviewToReject, setReviewToReject] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectReasonError, setRejectReasonError] = useState('');
    const [rejecting, setRejecting] = useState(false);

    // Heal at India Reply Modal states
    const [replyModalOpen, setReplyModalOpen] = useState(false);
    const [replyingReview, setReplyingReview] = useState(null);
    const [healAtIndiaReplyText, setHealAtIndiaReplyText] = useState('');
    const [submittingReply, setSubmittingReply] = useState(false);

    // Edit Host Reply Modal states
    const [editHostReplyModalOpen, setEditHostReplyModalOpen] = useState(false);
    const [editingHostReplyReview, setEditingHostReplyReview] = useState(null);
    const [editedHostReplyText, setEditedHostReplyText] = useState('');

    // Debounce search input
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return String(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Fetch Reviews from API with query params
    const fetchReviews = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params = {};

            // Tab-based status / external link filter
            if (activeTab === 'pending') {
                params.status = 'pending';
            } else if (activeTab === 'publish') {
                params.status = 'publish';
            } else if (activeTab === 'external') {
                params.isexternalLinkByHost = true;
            }

            // Search parameter
            if (debouncedSearch.trim()) {
                params.search = debouncedSearch.trim();
            }

            const response = await getAllReviews(params);

            if (response && response.success) {
                setReviews(response.data || []);
                if (response.statusCounts) {
                    setStatusCounts(response.statusCounts);
                }
            } else {
                setError(response?.message || "Failed to load reviews.");
            }
        } catch (err) {
            console.error("Error fetching reviews:", err);
            setError("Failed to fetch reviews. Please check backend connection.");
        } finally {
            setLoading(false);
        }
    }, [activeTab, debouncedSearch]);

    // Fetch Platform Rating Summary (GET /reviews/platform-rating-summary?limit=100)
    const fetchRatingSummary = useCallback(async () => {
        try {
            setLoadingSummary(true);
            const response = await getPlatformRatingSummary({ limit: 100 });
            if (response && response.success && response.data) {
                setRatingSummary(response.data);
            }
        } catch (err) {
            console.warn("Could not fetch platform rating summary, using defaults:", err);
        } finally {
            setLoadingSummary(false);
        }
    }, []);

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews]);

    useEffect(() => {
        fetchRatingSummary();
    }, [fetchRatingSummary]);

    // Review Actions: Approve / Publish (PATCH /reviews/:id/status with { status: "publish" })
    const handleApproveReview = async (id) => {
        try {
            setActionLoadingId(id);
            const response = await updateReviewStatus(id, { status: 'publish' });
            showSnackbar(response?.message || "Review approved & published successfully", "success");
            fetchReviews();
            fetchRatingSummary();
        } catch (err) {
            console.error("Error approving review:", err);
            showSnackbar(err.response?.data?.message || "Failed to publish review", "error");
        } finally {
            setActionLoadingId(null);
        }
    };

    // Review Actions: Open Reject Confirmation Modal
    const handleOpenRejectModal = (item) => {
        setReviewToReject(item);
        setRejectReason('');
        setRejectReasonError('');
        setRejectModalOpen(true);
    };

    // Review Actions: Confirm Reject (PATCH /reviews/:id/status with { status: "reject", rejectReason: ... })
    const handleConfirmReject = async () => {
        if (!reviewToReject) return;
        if (!rejectReason.trim()) {
            setRejectReasonError('Rejection reason is required.');
            return;
        }
        try {
            setRejecting(true);
            const response = await updateReviewStatus(reviewToReject.id, {
                status: 'reject',
                rejectReason: rejectReason.trim()
            });
            showSnackbar(response?.message || "Review has been rejected", "info");
            setRejectModalOpen(false);
            setReviewToReject(null);
            setRejectReason('');
            setRejectReasonError('');
            fetchReviews();
            fetchRatingSummary();
        } catch (err) {
            console.error("Error rejecting review:", err);
            showSnackbar(err.response?.data?.message || "Failed to reject review", "error");
        } finally {
            setRejecting(false);
        }
    };



    // Review Actions: Flag to Host
    const handleFlagToHost = (id) => {
        showSnackbar("Low-rating review flagged to retreat host for follow-up", "info");
    };

    // Host Reply Moderation
    const handleApproveHostReply = async (id) => {
        try {
            setActionLoadingId(id);
            await updateReviewStatus(id, { replayStatus: 'approved' });
            showSnackbar("Host reply approved and published", "success");
            fetchReviews();
        } catch (err) {
            showSnackbar(err.response?.data?.message || "Failed to approve host reply", "error");
        } finally {
            setActionLoadingId(null);
        }
    };

    // const handleRejectHostReply = async (id) => {
    //     try {
    //         setActionLoadingId(id);
    //         await updateReviewStatus(id, { replayStatus: 'rejected', replayDescription: null });
    //         showSnackbar("Host reply rejected due to policy breach", "warning");
    //         fetchReviews();
    //     } catch (err) {
    //         showSnackbar(err.response?.data?.message || "Failed to reject host reply", "error");
    //     } finally {
    //         setActionLoadingId(null);
    //     }
    // };

    // const handleOpenEditHostReply = (review) => {
    //     setEditingHostReplyReview(review);
    //     setEditedHostReplyText(review.replayDescription || '');
    //     setEditHostReplyModalOpen(true);
    // };

    const handleSaveEditedHostReply = async () => {
        if (!editingHostReplyReview) return;
        try {
            await updateReviewStatus(editingHostReplyReview.id, {
                replayDescription: editedHostReplyText,
                replayStatus: 'approved'
            });
            showSnackbar("Host reply edited and approved", "success");
            setEditHostReplyModalOpen(false);
            setEditingHostReplyReview(null);
            fetchReviews();
        } catch (err) {
            showSnackbar(err.response?.data?.message || "Failed to save host reply", "error");
        }
    };

    // Official Heal at India Reply
    const handleOpenReplyModal = (review) => {
        setReplyingReview(review);
        setHealAtIndiaReplyText(review.replayDescription || '');
        setReplyModalOpen(true);
    };

    const handleSendHealAtIndiaReply = async () => {
        if (!healAtIndiaReplyText.trim() || !replyingReview) return;
        try {
            setSubmittingReply(true);
            const response = await replyToReview(replyingReview.id, {
                replayDescription: healAtIndiaReplyText.trim()
            });
            showSnackbar(response?.message || "Official reply from Heal at India published", "success");
            setReplyModalOpen(false);
            setReplyingReview(null);
            setHealAtIndiaReplyText('');
            fetchReviews();
        } catch (err) {
            console.error("Error posting official reply:", err);
            showSnackbar(err.response?.data?.message || "Failed to post official reply", "error");
        } finally {
            setSubmittingReply(false);
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box
                sx={{
                    p: { xs: 2, md: 3 },
                    bgcolor: '#FBFBFB',
                    minHeight: '100vh',
                    fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif"
                }}
            >
                {/* 1. TOP HEADER TAB PILLS (Pending, Verified / Published, External / host collected) */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1.25,
                        mb: 2.5
                    }}
                >
                    {/* Pending Pill */}
                    <Box
                        onClick={() => setActiveTab('pending')}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.75,
                            py: 0.65,
                            borderRadius: '20px',
                            cursor: 'pointer',
                            fontSize: '0.78rem',
                            fontWeight: 500,
                            border: activeTab === 'pending' ? '1px solid #803927' : '1px dashed #CBD5E1',
                            bgcolor: activeTab === 'pending' ? '#803927' : '#FFFFFF',
                            color: activeTab === 'pending' ? '#FFFFFF' : '#475569',
                            boxShadow: 'none',
                            userSelect: 'none'
                        }}
                    >
                        Pending {statusCounts.pending ?? 0}
                    </Box>

                    {/* Verified / Published Pill */}
                    <Box
                        onClick={() => setActiveTab('publish')}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.75,
                            py: 0.65,
                            borderRadius: '20px',
                            cursor: 'pointer',
                            fontSize: '0.78rem',
                            fontWeight: 500,
                            border: activeTab === 'publish' ? '1px solid #803927' : '1px dashed #CBD5E1',
                            bgcolor: activeTab === 'publish' ? '#803927' : '#FFFFFF',
                            color: activeTab === 'publish' ? '#FFFFFF' : '#475569',
                            boxShadow: 'none',
                            userSelect: 'none'
                        }}
                    >
                        Verified {statusCounts.publish ?? 0}
                    </Box>

                    {/* External / Host Collected Pill */}
                    <Box
                        onClick={() => setActiveTab('external')}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.75,
                            py: 0.65,
                            borderRadius: '20px',
                            cursor: 'pointer',
                            fontSize: '0.78rem',
                            fontWeight: 500,
                            border: activeTab === 'external' ? '1px solid #803927' : '1px dashed #CBD5E1',
                            bgcolor: activeTab === 'external' ? '#803927' : '#FFFFFF',
                            color: activeTab === 'external' ? '#FFFFFF' : '#475569',
                            boxShadow: 'none',
                            userSelect: 'none'
                        }}
                    >
                        External / host collected {statusCounts.isexternalLinkByHost ?? 0}
                    </Box>
                </Box>

                {/* 2. SEARCH BAR ROW (Sends search terms to API) */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 2.5
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: '#FFFFFF',
                            border: '1px dashed #CBD5E1',
                            borderRadius: '6px',
                            px: 1.5,
                            py: 0.5,
                            width: { xs: '100%', sm: 360 },
                            height: 36
                        }}
                    >
                        <Search size={15} color="#94A3B8" style={{ marginRight: 8, flexShrink: 0 }} />
                        <input
                            type="text"
                            placeholder="Search review text or guest..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                border: 'none',
                                outline: 'none',
                                width: '100%',
                                fontSize: '0.78rem',
                                color: '#1E293B',
                                background: 'transparent',
                                fontFamily: 'inherit'
                            }}
                        />
                        {searchQuery && (
                            <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ p: 0.25, color: '#94A3B8' }}>
                                <X size={13} />
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2.5, borderRadius: '6px' }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* 3. MAIN CONTENT: 2-COLUMN LAYOUT (LEFT: REVIEWS ALL DETAILS, RIGHT: RATING SUMMARY) */}
                <Grid container spacing={2.5}>
                    {/* LEFT COLUMN: REVIEWS LIST DISPLAYING REQUIRED DETAILS */}
                    <Grid item size={{ xs: 12, lg: 8.7 }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                <CircularProgress size={26} sx={{ color: '#FFBD65' }} />
                            </Box>
                        ) : reviews.length > 0 ? (
                            <Stack spacing={2}>
                                {reviews.map((item) => {
                                    // Derived details from API response fields
                                    const guestName = item.showAsName?.trim() || item.cuName?.trim() || item.uuName?.trim() || 'Anonymous guest';
                                    const initialChar = guestName.charAt(0).toUpperCase() || 'G';
                                    const overallRatingNum = parseFloat(item.overallRating) || 0;
                                    const isLowRating = overallRatingNum <= 2.0;
                                    const isExternal = Boolean(item.isexternalLinkByHost);
                                    const isPublish = item.status === 'publish';
                                    const isPending = item.status === 'pending';
                                    const isReject = item.status === 'reject';
                                    const isUpdatingThis = actionLoadingId === item.id;

                                    // Category ratings list from API fields
                                    const categoryRatings = [
                                        { label: 'Location', value: item.location },
                                        { label: 'Value', value: item.valueForMoney },
                                        { label: 'Teachers', value: item.staffBehavior || item.programQuality },
                                        { label: 'Accommodation', value: item.accommodation },
                                        { label: 'Accuracy', value: item.descriptionAccuracy },
                                        { label: 'Food', value: item.foodQuality },
                                        { label: 'Communication', value: item.communication },
                                        { label: 'Overall', value: item.overallRating }
                                    ].filter(c => c.value !== undefined && c.value !== null && c.value !== '');

                                    return (
                                        <Paper
                                            key={item.id}
                                            elevation={0}
                                            sx={{
                                                bgcolor: '#FFFFFF',
                                                border: '1px solid #E2E8F0',
                                                borderRadius: '8px',
                                                p: { xs: 2, md: 2.5 },
                                                boxShadow: 'none'
                                            }}
                                        >
                                            {/* Card Top: Avatar, Guest Name, Badge & Status */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar
                                                        src={item.reviewerImage || undefined}
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            bgcolor: '#FAF5EE',
                                                            color: '#3D1F10',
                                                            fontSize: '0.8rem',
                                                            fontWeight: 600,
                                                            border: '1px solid #E2E8F0'
                                                        }}
                                                    >
                                                        {initialChar}
                                                    </Avatar>
                                                    <Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                            <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B', lineHeight: 1.2 }}>
                                                                {guestName}
                                                            </Typography>
                                                            <Chip
                                                                size="small"
                                                                label={isExternal ? 'external — host link' : 'verified booking'}
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: '0.68rem',
                                                                    fontWeight: 500,
                                                                    borderRadius: '12px',
                                                                    px: 0.5,
                                                                    bgcolor: isExternal ? '#EFF6FF' : '#F0FDF4',
                                                                    color: isExternal ? '#0284C7' : '#166534',
                                                                    border: `1px solid ${isExternal ? '#BAE6FD' : '#BBF7D0'}`
                                                                }}
                                                            />
                                                        </Box>
                                                        <Typography sx={{ fontSize: '0.74rem', color: '#64748B', mt: 0.35 }}>
                                                            {item.retreatName || item.centerName || 'Retreat'}
                                                            {item.bookingId ? ` · booking #${item.bookingId}` : ''}
                                                            {` · ${formatDate(item.createdAt)}`}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* Status Badge */}
                                                <Chip
                                                    size="small"
                                                    label={isReject ? 'rejected' : (isLowRating ? 'low rating' : (isPublish ? 'published' : (isPending ? 'pending' : (item.status || 'pending'))))}
                                                    sx={{
                                                        height: 20,
                                                        fontSize: '0.68rem',
                                                        fontWeight: 500,
                                                        borderRadius: '12px',
                                                        px: 0.5,
                                                        bgcolor: isReject ? '#FEF2F2' : (isLowRating ? '#FEF2F2' : (isPublish ? '#F0FDF4' : '#FFFBEB')),
                                                        color: isReject ? '#DC2626' : (isLowRating ? '#DC2626' : (isPublish ? '#166534' : '#D97706')),
                                                        border: `1px solid ${isReject || isLowRating ? '#FECACA' : (isPublish ? '#BBF7D0' : '#FDE68A')}`
                                                    }}
                                                />
                                            </Box>

                                            {/* Star Rating */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, mb: 1 }}>
                                                <Rating
                                                    value={overallRatingNum}
                                                    readOnly
                                                    precision={0.1}
                                                    size="small"
                                                    sx={{
                                                        color: '#FFBD65',
                                                        fontSize: '0.95rem'
                                                    }}
                                                />
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1E293B' }}>
                                                    {item.overallRating}
                                                </Typography>
                                            </Box>

                                            {/* Review Quote Text */}
                                            {item.title && (
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.84rem',
                                                        color: '#334155',
                                                        fontStyle: 'italic',
                                                        lineHeight: 1.5,
                                                        mb: 1.75
                                                    }}
                                                >
                                                    "{item.title}"
                                                </Typography>
                                            )}

                                            {/* CATEGORY RATINGS SECTION (Only displayed if category ratings exist in API response) */}
                                            {categoryRatings.length > 0 && (
                                                <Box sx={{ mt: 1.5, mb: 2 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#64748B', letterSpacing: '0.05em' }}>
                                                            CATEGORY RATINGS
                                                        </Typography>
                                                    </Box>

                                                    {/* 4-column category rating grid */}
                                                    <Grid container spacing={1.5}>
                                                        {categoryRatings.map((cat, idx) => (
                                                            <Grid item xs={6} sm={3} key={idx}>
                                                                <Typography sx={{ fontSize: '0.74rem', color: '#64748B' }}>
                                                                    {cat.label}{' '}
                                                                    <span style={{ color: '#1E293B', fontWeight: 600, marginLeft: 4 }}>
                                                                        {parseFloat(cat.value).toFixed(1)}
                                                                    </span>
                                                                </Typography>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Box>
                                            )}

                                            {/* HOST REPLY SUB-BOX (Only displayed if host reply exists in API response) */}
                                            {item.replayDescription && (
                                                <Box
                                                    sx={{
                                                        bgcolor: '#FFFDF9',
                                                        border: '1px solid #F6EBD9',
                                                        borderRadius: '6px',
                                                        p: 1.75,
                                                        my: 2
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 500, color: '#64748B' }}>
                                                            {item.replayByUserName || item.centerName || 'Host'} · {item.replayStatus || 'awaiting moderation'}
                                                        </Typography>

                                                    </Box>

                                                    <Typography sx={{ fontSize: '0.8rem', fontStyle: 'italic', color: '#334155', mb: 1 }}>
                                                        "{item.replayDescription}"
                                                    </Typography>
                                                </Box>
                                            )}


                                            {/* CARD FOOTER ACTIONS */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: 1.5,
                                                    mt: 2
                                                }}
                                            >
                                                {/* Action buttons on left */}
                                                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                                                    {/* Approve / Approve & Publish (PATCH status: "publish") */}
                                                    {!isPublish && (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            disableElevation disableRipple
                                                            disabled={isUpdatingThis}
                                                            onClick={() => handleApproveReview(item.id)}
                                                            sx={{
                                                                bgcolor: '#6E8964',
                                                                color: '#FFFFFF',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 500,
                                                                textTransform: 'none',
                                                                px: 1.75,
                                                                py: 0.45,
                                                                borderRadius: '4px',
                                                                boxShadow: 'none',
                                                                '&:hover': { bgcolor: '#5C7453' }
                                                            }}
                                                        >
                                                            {isUpdatingThis ? 'Publishing...' : (categoryRatings.length > 0 ? 'Approve & publish' : 'Approve')}
                                                        </Button>
                                                    )}



                                                    {/* Reject (PATCH status: "reject") */}
                                                    {!isReject && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            disableElevation disableRipple
                                                            disabled={isUpdatingThis}
                                                            onClick={() => handleOpenRejectModal(item)}
                                                            sx={{
                                                                borderColor: '#803927',
                                                                color: '#803927',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 500,
                                                                textTransform: 'none',
                                                                px: 1.5,
                                                                py: 0.45,
                                                                borderRadius: '4px',
                                                                boxShadow: 'none',
                                                                '&:hover': { borderColor: '#5C2819', bgcolor: '#FAF5EE' }
                                                            }}
                                                        >
                                                            Reject
                                                        </Button>
                                                    )}

                                                    {/* Reply as Heal at India */}
                                                    {/* <Button
                                                        size="small"
                                                        variant="outlined"
                                                        disableElevation disableRipple
                                                        onClick={() => handleOpenReplyModal(item)}
                                                        sx={{
                                                            border: '1px dashed #CBD5E1',
                                                            color: '#475569',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500,
                                                            textTransform: 'none',
                                                            px: 1.5,
                                                            py: 0.45,
                                                            borderRadius: '4px',
                                                            boxShadow: 'none',
                                                            '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' }
                                                        }}
                                                    >
                                                        {item.replayDescription ? 'Edit reply' : 'Reply as Heal at India'}
                                                    </Button> */}



                                                    {/* View Details Button (Navigates to new page) */}
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        disableElevation disableRipple
                                                        startIcon={<Eye size={14} />}
                                                        onClick={() => navigate(`/review-management/reviews/${item.id}`)}
                                                        sx={{
                                                            borderColor: '#E2E8F0',
                                                            color: '#475569',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500,
                                                            textTransform: 'none',
                                                            px: 1.5,
                                                            py: 0.45,
                                                            borderRadius: '4px',
                                                            boxShadow: 'none',
                                                            '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                                                        }}
                                                    >
                                                        View Details
                                                    </Button>
                                                </Stack>

                                            </Box>
                                        </Paper>
                                    );
                                })}
                            </Stack>
                        ) : (
                            <Paper sx={{ p: 5, textAlign: 'center', bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px' }} elevation={0}>
                                <Typography sx={{ fontSize: '0.85rem', color: '#64748B' }}>
                                    No {activeTab === 'pending' ? 'pending' : activeTab === 'publish' ? 'published' : 'external'} reviews found.
                                </Typography>
                            </Paper>
                        )}
                    </Grid>

                    {/* RIGHT COLUMN: RATING SUMMARY PANEL */}
                    <Grid item size={{ xs: 12, lg: 3.3 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                bgcolor: '#FFFFFF',
                                border: '1px solid #E2E8F0',
                                borderRadius: '8px',
                                p: 2.5,
                                boxShadow: 'none',
                                position: { lg: 'sticky' },
                                top: { lg: 20 }
                            }}
                        >
                            {/* Heading */}
                            <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                Rating summary
                            </Typography>

                            {/* Overall Score Row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                                <Typography sx={{ fontSize: '2.4rem', fontWeight: 600, color: '#1E293B', lineHeight: 1 }}>
                                    {ratingSummary?.overallScore !== undefined ? Number(ratingSummary.overallScore).toFixed(1) : '—'}
                                </Typography>
                                <Box>
                                    <Rating
                                        value={parseFloat(ratingSummary?.overallScore) || 0}
                                        precision={0.1}
                                        readOnly
                                        size="small"
                                        sx={{ color: '#FFBD65', fontSize: '0.95rem' }}
                                    />
                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B', mt: 0.25 }}>
                                        from {ratingSummary?.totalPublishedReviews ?? 0} published reviews
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Star breakdown bars */}
                            <Box sx={{ mb: 3 }}>
                                {(ratingSummary?.starBreakdown || []).map((item) => {
                                    const barColor = item.color || getStarColor(item.stars);
                                    return (
                                        <Box
                                            key={item.stars}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.25,
                                                mb: 1
                                            }}
                                        >
                                            {/* Star label */}
                                            <Typography sx={{ fontSize: '0.72rem', color: '#475569', fontWeight: 500, width: 22, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                {item.stars}★
                                            </Typography>

                                            {/* Progress Track & Bar */}
                                            <Box sx={{ flex: 1, height: 6, bgcolor: '#F1F5F9', borderRadius: '3px', overflow: 'hidden' }}>
                                                <Box
                                                    sx={{
                                                        width: `${item.percentage || 0}%`,
                                                        height: '100%',
                                                        bgcolor: barColor,
                                                        borderRadius: '3px'
                                                    }}
                                                />
                                            </Box>

                                            {/* Percentage */}
                                            <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 500, width: 28, textAlign: 'right' }}>
                                                {item.percentage || 0}%
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>

                            <Box sx={{ borderBottom: '1px solid #F1F5F9', mb: 2.5 }} />

                            {/* CATEGORY AVERAGES SECTION */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#64748B', letterSpacing: '0.05em' }}>
                                    CATEGORY AVERAGES
                                </Typography>
                            </Box>

                            {/* Category Progress Bars */}
                            <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                                {(ratingSummary?.categoryAverages || []).map((cat) => {
                                    const scoreNum = parseFloat(cat.score) || 0;
                                    const barColor = cat.color || getCategoryColor(cat.category, scoreNum);
                                    return (
                                        <Box key={cat.category}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                <Typography sx={{ fontSize: '0.74rem', color: '#475569', fontWeight: 400 }}>
                                                    {cat.category}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.74rem', color: '#1E293B', fontWeight: 600 }}>
                                                    {scoreNum.toFixed(1)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ width: '100%', height: 6, bgcolor: '#F1F5F9', borderRadius: '3px', overflow: 'hidden' }}>
                                                <Box
                                                    sx={{
                                                        width: `${Math.min(100, (scoreNum / 5) * 100)}%`,
                                                        height: '100%',
                                                        bgcolor: barColor,
                                                        borderRadius: '3px'
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Stack>

                            {/* Site Insight / Weak Point Note at Bottom */}
                            {(ratingSummary?.siteWeakPointNote) && (
                                <Box sx={{ borderTop: '1px solid #F1F5F9', pt: 1.75 }}>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B', lineHeight: 1.45, fontStyle: 'italic' }}>
                                        {ratingSummary?.siteWeakPointNote}
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                {/* MODAL 1: REJECT CONFIRMATION MODAL */}
                <Dialog
                    open={rejectModalOpen}
                    onClose={() => !rejecting && setRejectModalOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            boxShadow: 'none',
                            bgcolor: '#FFFFFF'
                        }
                    }}
                >
                    <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#1E293B' }}>
                            Reject Review
                        </Typography>
                        <IconButton size="small" onClick={() => !rejecting && setRejectModalOpen(false)} sx={{ color: '#64748B' }}>
                            <X size={16} />
                        </IconButton>
                    </Box>
                    <DialogContent sx={{ p: 2.5 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#475569', mb: 1.5 }}>
                            Are you sure you want to reject this review? Please provide a mandatory reason for this action.
                        </Typography>
                        {reviewToReject && (
                            <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', mb: 2 }}>
                                <Typography sx={{ fontSize: '0.76rem', fontWeight: 600, color: '#1E293B' }}>
                                    {reviewToReject.showAsName || reviewToReject.cuName || 'Guest Review'}
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mt: 0.25 }}>
                                    {reviewToReject.retreatName || reviewToReject.centerName || 'Retreat'}
                                </Typography>
                                {reviewToReject.title && (
                                    <Typography sx={{ fontSize: '0.74rem', color: '#475569', mt: 0.75, fontStyle: 'italic', lineHeight: 1.4 }}>
                                        "{reviewToReject.title}"
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {/* Mandatory Rejection Reason */}
                        <Box sx={{ mt: 1.5 }}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#475569', mb: 0.75 }}>
                                Reason for Rejection <span style={{ color: '#DC2626' }}>*</span>
                            </Typography>
                            <TextField
                                multiline
                                rows={3}
                                fullWidth
                                placeholder="Specify the reason for rejecting this review (mandatory)..."
                                value={rejectReason}
                                onChange={(e) => {
                                    setRejectReason(e.target.value);
                                    if (rejectReasonError && e.target.value.trim()) {
                                        setRejectReasonError('');
                                    }
                                }}
                                error={Boolean(rejectReasonError)}
                                helperText={rejectReasonError}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: '0.8rem',
                                        borderRadius: '6px',
                                        '& fieldset': { borderColor: rejectReasonError ? '#DC2626' : '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: rejectReasonError ? '#DC2626' : '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: rejectReasonError ? '#DC2626' : '#803927' }
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontSize: '0.7rem',
                                        mx: 0.5
                                    }
                                }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 2.5, py: 1.75, borderTop: '1px solid #F1F5F9' }}>
                        <Button
                            size="small"
                            onClick={() => setRejectModalOpen(false)}
                            disabled={rejecting}
                            sx={{ color: '#64748B', fontSize: '0.75rem', textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            disabled={rejecting || !rejectReason.trim()}
                            onClick={handleConfirmReject}
                            sx={{
                                bgcolor: '#803927',
                                color: '#FFFFFF',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                px: 2,
                                py: 0.5,
                                borderRadius: '4px',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#5C2819' }
                            }}
                        >
                            {rejecting ? 'Rejecting...' : 'Confirm Reject'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* MODAL 2: REPLY AS HEAL AT INDIA */}
                <Dialog
                    open={replyModalOpen}
                    onClose={() => setReplyModalOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            boxShadow: 'none',
                            bgcolor: '#FFFFFF'
                        }
                    }}
                >
                    <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#1E293B' }}>
                            {replyingReview?.replayDescription ? 'Edit Reply as Heal at India' : 'Reply as Heal at India'}
                        </Typography>
                        <IconButton size="small" onClick={() => setReplyModalOpen(false)} sx={{ color: '#64748B' }}>
                            <X size={16} />
                        </IconButton>
                    </Box>
                    <DialogContent sx={{ p: 2.5 }}>
                        {replyingReview && (
                            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                                <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, color: '#1E293B' }}>
                                    Replying to review by {replyingReview.showAsName || replyingReview.cuName || 'Guest'}
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mt: 0.35, fontStyle: 'italic' }}>
                                    "{replyingReview.title}"
                                </Typography>
                            </Box>
                        )}
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#475569', mb: 1 }}>
                            Official Response
                        </Typography>
                        <TextField
                            multiline
                            rows={4}
                            fullWidth
                            placeholder="Write official response on behalf of Heal at India..."
                            value={healAtIndiaReplyText}
                            onChange={(e) => setHealAtIndiaReplyText(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontSize: '0.8rem',
                                    borderRadius: '6px',
                                    '& fieldset': { borderColor: '#E2E8F0' },
                                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                                    '&.Mui-focused fieldset': { borderColor: '#6E8964' }
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 2.5, py: 1.75, borderTop: '1px solid #F1F5F9' }}>
                        <Button
                            size="small"
                            onClick={() => setReplyModalOpen(false)}
                            sx={{ color: '#64748B', fontSize: '0.75rem', textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            disabled={submittingReply || !healAtIndiaReplyText.trim()}
                            onClick={handleSendHealAtIndiaReply}
                            sx={{
                                bgcolor: '#6E8964',
                                color: '#FFFFFF',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                px: 2,
                                py: 0.5,
                                borderRadius: '4px',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#5C7453' }
                            }}
                        >
                            {submittingReply ? 'Posting...' : (replyingReview?.replayDescription ? 'Update Reply' : 'Post Official Reply')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* MODAL 3: EDIT & APPROVE HOST REPLY */}
                <Dialog
                    open={editHostReplyModalOpen}
                    onClose={() => setEditHostReplyModalOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            boxShadow: 'none',
                            bgcolor: '#FFFFFF'
                        }
                    }}
                >
                    <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#1E293B' }}>
                            Edit & Approve Host Reply
                        </Typography>
                        <IconButton size="small" onClick={() => setEditHostReplyModalOpen(false)} sx={{ color: '#64748B' }}>
                            <X size={16} />
                        </IconButton>
                    </Box>
                    <DialogContent sx={{ p: 2.5 }}>
                        <Alert severity="warning" sx={{ mb: 2, fontSize: '0.72rem', borderRadius: '6px' }}>
                            Remove policy-breaching content (phone numbers, personal emails, off-platform price offers) before approving.
                        </Alert>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#475569', mb: 1 }}>
                            Host Reply Text
                        </Typography>
                        <TextField
                            multiline
                            rows={3}
                            fullWidth
                            value={editedHostReplyText}
                            onChange={(e) => setEditedHostReplyText(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontSize: '0.8rem',
                                    borderRadius: '6px',
                                    '& fieldset': { borderColor: '#E2E8F0' },
                                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                                    '&.Mui-focused fieldset': { borderColor: '#6E8964' }
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 2.5, py: 1.75, borderTop: '1px solid #F1F5F9' }}>
                        <Button
                            size="small"
                            onClick={() => setEditHostReplyModalOpen(false)}
                            sx={{ color: '#64748B', fontSize: '0.75rem', textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            onClick={handleSaveEditedHostReply}
                            disabled={!editedHostReplyText.trim()}
                            sx={{
                                bgcolor: '#6E8964',
                                color: '#FFFFFF',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                px: 2,
                                py: 0.5,
                                borderRadius: '4px',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#5C7453' }
                            }}
                        >
                            Save & Approve
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>
        </PermissionWrapper>
    );
};

export default Reviews;
